/**
 * Wording for Clone from Global / Clone from Machine (KAN-518: a cloned standalone machine owns copies of its layout and frame files).
 * Pure functions, no Angular imports. All user-facing strings of the clone confirmation and result live here (PO-confirmed wording).
 */

/** What the target machine already holds, as reported by `cloneDeviceConfiguration` with `inspectOnly`. */
export interface CloneTargetState { hasData: boolean; frameGroups: number; files: number }

export const CLONE_GLOBAL_CONFIRM = 'Copy the global layout, frame groups and artwork into this machine? This machine will then use only its own copies. Files already on this machine are kept.';
export const CLONE_FAILED = 'Copy failed. This machine was not changed.';
export const CLONE_COPY_LABEL = 'Copy';
export const CLONE_REPLACE_LABEL = 'Replace layout';

export function cloneReplaceLine(state: CloneTargetState): string {
  return `This machine already has ${state.frameGroups} frame groups and ${state.files} files. The layout and frame groups will be replaced; existing files are not deleted.`;
}

/** KAN-521 Clone from Machine confirmation (only layout artwork and videos are copied). */
export function cloneMachineConfirmText(sourceName: string): string {
  return `Copy the layout, frame groups, artwork and videos from ${sourceName} to this machine? Session photos, logs and other files are not copied. Files already on this machine are kept.`;
}

export interface CloneConfirmation { lines: string[]; confirmLabel: string; replace: boolean }

/**
 * Confirmation shown before every clone (KAN-518 Clone from Global, KAN-521 Clone from Machine), with the replace line when the
 * machine already has layout or frame group data.
 */
export function cloneConfirmation(source: 'global' | 'device', state: CloneTargetState, sourceName: string = ''): CloneConfirmation {
  const lines = [source === 'global' ? CLONE_GLOBAL_CONFIRM : cloneMachineConfirmText(sourceName)];
  if (state.hasData) lines.push(cloneReplaceLine(state));
  return { lines, confirmLabel: state.hasData ? CLONE_REPLACE_LABEL : CLONE_COPY_LABEL, replace: state.hasData };
}

const count = (v: any) => (Number.isFinite(v) && v >= 0 ? v : 0);

/**
 * Result text from the callable's counts (missing or malformed counts read as 0). Clone from Machine adds that sessions and logs were
 * not copied and, when there are any, how many older files on the machine were kept (KAN-521).
 */
export function cloneResultMessage(result: any, source: 'global' | 'device' = 'global'): string {
  const first = `Copied ${count(result?.frameGroupsCopied)} frame groups and ${count(result?.assetsCopied)} files.`;
  if (source === 'global') return first;
  const kept = count(result?.olderFilesKept);
  return kept > 0
    ? `${first} Sessions and logs were not copied. ${kept} older files on this machine were kept.`
    : `${first} Sessions and logs were not copied.`;
}

/** Error text; the function's own message (if any) follows the confirmed sentence. */
export function cloneErrorMessage(detail: string | null | undefined): string {
  const d = (detail || '').trim();
  return d && d !== 'internal' ? `${CLONE_FAILED} ${d}` : CLONE_FAILED;
}
